import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ParkCatalogIndex, PLAN_NAMES, PLAN_ORDER, offerOfProvider, packagesOf } from '../park-flow/park-catalog';

export interface IParkMealChoice {
  plan: string;   // N | Q | T | D
  offer: any;     // oferta del roomCatalog para ese plan
}

interface IMealInfo {
  title: string;
  description: string;
  chips: string[];
  includes: string[];
}

export interface IParkMealCard extends IMealInfo {
  plan: string;
  offer: any;
  price: number;      // total del paquete (con rentabilidad si la habitación real está en la respuesta)
  extra: number;      // diferencia contra el paquete sin plan
  currency: string;
}

// Textos de cada plan (Disney 2027, según la nota de Octopus en remarks).
const MEALS: { [plan: string]: IMealInfo } = {
  N: {
    title: 'No Deseo Agregar Plan de Comidas',
    description: 'Organiza tus comidas libremente y paga directamente en los restaurantes.',
    chips: ['Flexible'],
    includes: [],
  },
  Q: {
    title: 'Plan de Comidas de Servicio Rápido',
    description: 'Ideal para aprovechar el día en los parques, con comidas prácticas y flexibles.',
    chips: ['Restaurantes de Mostrador', 'Sin Mesero'],
    includes: [
      '2 comidas por persona por noche de estadía (plato principal + bebida).',
      '1 snack o bebida seleccionada por persona por noche.',
      'Vaso rellenable en el hotel Disney (para estaciones de bebidas habilitadas)',
    ],
  },
  T: {
    title: 'Plan de Comidas con Servicio de Mesa',
    description: 'Para disfrutar de comidas más tranquilas, restaurantes temáticos y experiencias especiales.',
    chips: ['Restaurantes Temáticos', 'Con Mesero'],
    includes: [
      '1 comida de mesa por persona por noche, además de 1 comida de servicio rápido.',
      'Plato principal, bebida y postre (cuando corresponda).',
      'Snack y vaso rellenable (según plan).',
    ],
  },
  D: {
    title: 'Plan de Comidas Deluxe',
    description: 'Más comidas con servicio en mesa durante toda tu estadía, en restaurantes temáticos.',
    chips: ['Restaurantes Temáticos', 'Con Mesero'],
    includes: [
      '1 comida de servicio rápido y 2 comidas de mesa por persona por noche.',
      'Plato principal, bebida y postre (cuando corresponda).',
      '1 snack y vaso rellenable.',
    ],
  },
};

export const PARK_PLAN_NAMES = PLAN_NAMES;

/**
 * Paso "Plan de comidas": un plan por tarjeta para el tipo de habitación elegido, con el costo adicional
 * contra el paquete sin plan. Los precios salen del roomCatalog (paquetes N/Q/T/D, con o sin tickets).
 */
@Component({
  selector: 'usb-park-meals',
  templateUrl: './park-meals.component.html',
  styleUrls: ['./park-meals.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkMealsComponent implements OnChanges {
  @Input() roomType: any;               // tipo de habitación del roomCatalog
  @Input() providerId: string;          // solo se ofrecen los planes de este proveedor
  @Input() index: ParkCatalogIndex;     // precio con rentabilidad por habitación real
  @Input() withTickets = false;         // true: solo planes con tickets (T*); false: H* y, si no hay, T*
  @Input() selectedPlan = 'N';
  @Input() currencies: any[];
  @Input() currencyAgency: any;
  @Input() buttonColorActive: string;

  @Output() choose = new EventEmitter<IParkMealChoice>();

  // Fotos e íconos fijos de la vista (assets/img/parks/disney/meals y /icons).
  readonly basePath = '/assets/img/parks/disney/';

  cards: IParkMealCard[] = [];

  ngOnChanges(): void {
    if (!this.roomType || !this.index) {
      this.cards = [];
      return;
    }
    const packages = packagesOf(this.roomType);
    const offerFor = (prefix: string, plan: string) => {
      const offer = offerOfProvider(packages[prefix + plan], this.providerId);
      return offer && this.index.real(offer) ? offer : null;
    };
    // Sin tickets si el plan se vende así; si no, el paquete con tickets (T*). El costo extra es contra el mismo
    // paquete sin plan.
    const first = this.withTickets ? ['T'] : ['H', 'T'];

    this.cards = PLAN_ORDER
      .map(plan => {
        const prefix = first.find(p => !!offerFor(p, plan));
        return prefix ? { plan, prefix, offer: offerFor(prefix, plan) } : null;
      })
      .filter((c): c is { plan: string; prefix: string; offer: any } => !!c)
      .map(c => {
        const base = offerFor(c.prefix, 'N');
        const price = this.index.price(c.offer);
        return {
          ...MEALS[c.plan],
          plan: c.plan,
          offer: c.offer,
          price,
          extra: base ? Math.max(price - this.index.price(base), 0) : 0,
          currency: this.index.currency(c.offer),
        };
      });
  }

  trackByPlan(_: number, card: IParkMealCard): string {
    return card.plan;
  }
}
